/* istanbul ignore file */
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useApolloMeetings } from 'src/config/apollo-meetings';
import { useFlags } from 'src/config/feature-flags';
import useAccount from 'src/contexts/account';
import { useHubspot } from 'src/contexts/hubspot';
import { MeetingDialog } from './MeetingDialog';

export const MeetingDialogContainer: React.FC = () => {
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [meetingBooked, setMeetingBooked] = useState(false);

  const { currentCompany, userEmail, userFirstName, userLastName } =
    useAccount();
  const { hubspotData, isLoading: isHubspotLoading } = useHubspot();
  const { submit: submitApolloMeeting, isReady } = useApolloMeetings();
  const {
    'exp_04212025-meeting-dialog-override': LDFF_exp424MeetingDialogOverride,
  } = useFlags(['exp_04212025-meeting-dialog-override']);

  const daysSinceTrial = useMemo(() => {
    if (!currentCompany?.created_at) {
      return null;
    }

    const trialStartDate = new Date(Number(currentCompany.created_at) * 1000);
    const today = new Date();
    return Math.floor(
      (today.getTime() - trialStartDate.getTime()) / (1000 * 60 * 60 * 24),
    );
  }, [currentCompany?.created_at]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const shouldShowMeetingModal =
      // Not loading and is an automated trial
      !isHubspotLoading &&
      currentCompany?.billing_type === 'automated_trial' &&
      // At least 1 day into trial
      daysSinceTrial >= 1 &&
      // Has a scheduled meeting
      !hubspotData?.latestMeeting &&
      // Deal is in Experience pipeline
      (hubspotData?.latestDeal?.pipeline?.label === 'Experience' ||
        String(hubspotData?.latestDeal?.pipeline?.id) === '723082784');

    if (shouldShowMeetingModal || LDFF_exp424MeetingDialogOverride) {
      timeout = setTimeout(() => {
        setShowMeetingModal(true);
      }, 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [
    daysSinceTrial,
    hubspotData?.latestMeeting,
    hubspotData?.latestDeal,
    isHubspotLoading,
    currentCompany?.billing_type,
    LDFF_exp424MeetingDialogOverride,
  ]);

  const handleBookDemo = () => {
    if (!isReady) return;
    setShowMeetingModal(false);

    submitApolloMeeting(
      {
        email: String(userEmail ?? ''),
        full_name: `${String(userFirstName ?? '')} ${String(userLastName ?? '')}`,
        company_name: String(currentCompany?.name ?? ''),
        company_size: Number(hubspotData?.company?.company_size ?? 0),
        company_location: String(hubspotData?.company?.state ?? ''),
      },
      () => {
        setMeetingBooked(true);
      },
      {
        closeOnOutside: false,
        onClose: () => {
          setShowMeetingModal(false);
          setShowSuccessModal(true);
        },
      },
    );
  };

  return (
    <MeetingDialog
      showMeetingModal={showMeetingModal}
      showSuccessModal={showSuccessModal}
      meetingBooked={meetingBooked}
      onMeetingModalChange={setShowMeetingModal}
      onSuccessModalChange={setShowSuccessModal}
      onBookDemo={handleBookDemo}
    />
  );
};
